/**
 * SettlementLineItemRow - 정산 라인 아이템 행 컴포넌트
 *
 * 수업별 정산 내역 테이블의 단일 행을 렌더링한다.
 * 위탁대금 및 비고의 인라인 편집 기능을 제공하며, isEditable 여부에 따라 편집 버튼을 표시한다.
 */

import { useState } from "react";
import { Link } from "react-router-dom";
import type { SettlementLineItem } from "@vlab-corporation/vlab-pilates-service-interface/settlement";
import type { AdjustLineItemParams } from "@/services/admin-settlement-actions";

// --- 날짜 포맷 유틸리티 ---

/**
 * "YYYY-MM-DD" 형식의 날짜를 "MM.DD" 형식으로 변환한다.
 * @example formatClassDate("2026-02-10") → "02.10"
 */
function formatClassDate(dateStr: string): string {
  const parts = dateStr.split("-");
  if (parts.length !== 3) return dateStr;
  return `${parts[1]}.${parts[2]}`;
}

// --- Props 인터페이스 ---

export interface SettlementLineItemRowProps {
  /** 정산 라인 아이템 데이터 */
  item: SettlementLineItem;
  /** 센터 ID (회원 상세 링크 및 adjustLineItem 호출에 사용) */
  centerId: number;
  /** 강사 ID (adjustLineItem 호출에 사용) */
  instructorId: string;
  /** 정산 연월 "YYYY-MM" (adjustLineItem 호출에 사용) */
  yearMonth: string;
  /**
   * 인라인 편집 활성 여부.
   * settlement.status === "calculating"인 경우에만 true로 전달한다.
   */
  isEditable: boolean;
  /**
   * 라인 아이템 조정 콜백.
   * 위탁대금 또는 비고 저장 시 호출된다.
   */
  onAdjust: (params: AdjustLineItemParams) => Promise<void>;
  /**
   * 저장 성공 후 호출되는 콜백 (선택).
   * 정산 데이터 갱신 등의 부수 효과에 사용한다.
   */
  onAdjusted?: () => void;
}

/**
 * 정산 라인 아이템 테이블의 단일 행 컴포넌트.
 *
 * 렌더링 요소:
 * - 수업일 (classDate): "MM.DD" 형식
 * - 회원명 (memberName): memberId 있으면 Link, 없으면 텍스트
 * - 회원 유형 (memberType): "첫 구매"/"재등록" 텍스트
 * - 위탁대금 (unitPrice): originalUnitPrice 취소선 + 인라인 편집 지원
 * - 금액 (amount)
 * - 노쇼 여부 (isNoShow): 배지
 * - 비고 (notes): 인라인 편집 지원
 *
 * 인라인 편집 상태(editingUnitPrice, editingNotes)는 컴포넌트 내부 state로 관리한다.
 */
export function SettlementLineItemRow({
  item,
  centerId,
  instructorId,
  yearMonth,
  isEditable,
  onAdjust,
  onAdjusted,
}: SettlementLineItemRowProps) {
  // 위탁대금 편집 상태: undefined이면 편집 모드 아님, string이면 편집 중인 값
  const [editingUnitPrice, setEditingUnitPrice] = useState<string | undefined>(undefined);
  // 비고 편집 상태: undefined이면 편집 모드 아님, string이면 편집 중인 값
  const [editingNotes, setEditingNotes] = useState<string | undefined>(undefined);
  // 저장 에러 메시지: null이면 에러 없음
  const [saveError, setSaveError] = useState<string | null>(null);

  const isEditingPrice = editingUnitPrice !== undefined;
  const isEditingNote = editingNotes !== undefined;

  // --- 위탁대금 편집 핸들러 ---

  /** 위탁대금 인라인 편집을 시작한다. */
  const handleStartEditUnitPrice = () => {
    setEditingUnitPrice(String(item.unitPrice));
  };

  /** 위탁대금 편집을 취소한다. */
  const handleCancelEditUnitPrice = () => {
    setEditingUnitPrice(undefined);
  };

  /**
   * 위탁대금을 저장한다.
   * onAdjust 콜백을 호출하여 라인 아이템 위탁대금을 수정한다.
   * 유효하지 않은 값(NaN)이면 아무 동작도 하지 않는다.
   * 성공 시 onAdjusted 콜백을 호출한다.
   */
  const handleSaveUnitPrice = async () => {
    if (editingUnitPrice === undefined) return;

    const parsed = parseInt(editingUnitPrice, 10);
    if (isNaN(parsed)) return;

    try {
      await onAdjust({
        centerId,
        instructorId,
        yearMonth,
        classId: item.classId,
        unitPrice: parsed,
      });
      setEditingUnitPrice(undefined);
      setSaveError(null);
      onAdjusted?.();
    } catch {
      setSaveError("저장에 실패했습니다.");
    }
  };

  // --- 비고 편집 핸들러 ---

  /** 비고 인라인 편집을 시작한다. */
  const handleStartEditNotes = () => {
    setEditingNotes(item.notes ?? "");
  };

  /** 비고 편집을 취소한다. */
  const handleCancelEditNotes = () => {
    setEditingNotes(undefined);
  };

  /**
   * 비고를 저장한다.
   * onAdjust 콜백을 호출하여 라인 아이템 비고를 수정한다.
   * 성공 시 onAdjusted 콜백을 호출한다.
   */
  const handleSaveNotes = async () => {
    if (editingNotes === undefined) return;

    try {
      await onAdjust({
        centerId,
        instructorId,
        yearMonth,
        classId: item.classId,
        notes: editingNotes,
      });
      setEditingNotes(undefined);
      setSaveError(null);
      onAdjusted?.();
    } catch {
      setSaveError("저장에 실패했습니다.");
    }
  };

  return (
    <tr className="border-t border-gray-100">
      {/* 수업일 컬럼 */}
      <td className="px-3 py-2 text-gray-700">
        {item.classDate ? formatClassDate(item.classDate) : "-"}
      </td>

      {/* 회원명 컬럼: memberNames 배열 또는 단수 memberName 지원 */}
      <td className="px-3 py-2 text-gray-700">
        {item.memberNames && item.memberNames.length > 0 ? (
          item.memberNames.length > 1 ? (
            <span>
              {item.memberNames[0]} 외 {item.memberNames.length - 1}명
            </span>
          ) : item.studiomateMemberIds?.[0] ? (
            <Link
              to={`/centers/${centerId}/members/${item.studiomateMemberIds[0]}`}
              className="text-blue-600 hover:text-blue-800 hover:underline"
            >
              {item.memberNames[0]}
            </Link>
          ) : (
            <span>{item.memberNames[0]}</span>
          )
        ) : item.memberName ? (
          item.studiomateMemberId ? (
            <Link
              to={`/centers/${centerId}/members/${item.studiomateMemberId}`}
              className="text-blue-600 hover:text-blue-800 hover:underline"
            >
              {item.memberName}
            </Link>
          ) : (
            item.memberName
          )
        ) : (
          "-"
        )}
      </td>

      {/* 회원 유형 컬럼 */}
      <td className="px-3 py-2 text-gray-700">
        {item.memberType === null
          ? "N/A"
          : item.memberType === "firstPurchase"
            ? "첫 구매"
            : "재등록"}
      </td>

      {/* 위탁대금 컬럼 (인라인 편집) */}
      <td className="px-3 py-2 text-right text-gray-700">
        {isEditingPrice ? (
          <div className="flex items-center justify-end gap-1">
            <input
              type="number"
              aria-label="위탁대금 입력"
              value={editingUnitPrice}
              onChange={(e) => setEditingUnitPrice(e.target.value)}
              className="w-24 px-2 py-1 text-right text-sm border border-gray-300 rounded"
            />
            <button
              aria-label="위탁대금 저장"
              onClick={() => void handleSaveUnitPrice()}
              className="px-1.5 py-1 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700"
            >
              저장
            </button>
            <button
              aria-label="위탁대금 편집 취소"
              onClick={handleCancelEditUnitPrice}
              className="px-1.5 py-1 text-xs font-medium text-gray-600 bg-gray-100 rounded hover:bg-gray-200"
            >
              취소
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-end gap-1">
            <span>
              {item.unitPrice.toLocaleString("ko-KR")}원
              {item.originalUnitPrice !== undefined && (
                <span className="ml-1 text-xs text-gray-400 line-through">
                  {item.originalUnitPrice.toLocaleString("ko-KR")}원
                </span>
              )}
            </span>
            {isEditable && (
              <button
                aria-label="위탁대금 편집"
                onClick={handleStartEditUnitPrice}
                className="ml-1 p-0.5 text-gray-400 hover:text-gray-600 rounded hover:bg-gray-100"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <line x1="18" y1="2" x2="22" y2="6" />
                  <path d="M7.5 20.5 19 9l-4-4L3.5 16.5 2 22z" />
                </svg>
              </button>
            )}
          </div>
        )}
      </td>

      {/* 금액 컬럼 */}
      <td className="px-3 py-2 text-right">
        <span className="font-medium text-gray-900">{item.amount.toLocaleString("ko-KR")}원</span>
        <p className="text-xs text-gray-400">
          세후 {(item.amount - Math.round(item.amount * 0.033)).toLocaleString("ko-KR")}원
        </p>
      </td>

      {/* 비고 컬럼 (노쇼 배지 + 인라인 편집 + 에러 표시) */}
      <td className="px-3 py-2">
        {saveError && (
          <p role="alert" className="text-xs text-red-600 mb-1">
            {saveError}
          </p>
        )}
        <div className="flex items-center gap-1">
          {item.isNoShow && (
            <span className="inline-block px-1.5 py-0.5 text-xs font-semibold rounded-full bg-red-100 text-red-800">
              노쇼
            </span>
          )}
          {isEditingNote ? (
            <>
              <input
                type="text"
                aria-label="비고 입력"
                value={editingNotes}
                onChange={(e) => setEditingNotes(e.target.value)}
                className="w-32 px-2 py-1 text-sm border border-gray-300 rounded"
              />
              <button
                aria-label="비고 저장"
                onClick={() => void handleSaveNotes()}
                className="px-1.5 py-1 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700"
              >
                저장
              </button>
              <button
                aria-label="비고 편집 취소"
                onClick={handleCancelEditNotes}
                className="px-1.5 py-1 text-xs font-medium text-gray-600 bg-gray-100 rounded hover:bg-gray-200"
              >
                취소
              </button>
            </>
          ) : (
            <>
              <span className="text-gray-600">{item.notes ?? "-"}</span>
              {isEditable && (
                <button
                  aria-label="비고 편집"
                  onClick={handleStartEditNotes}
                  className="p-0.5 text-gray-400 hover:text-gray-600 rounded hover:bg-gray-100"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="13"
                    height="13"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <line x1="18" y1="2" x2="22" y2="6" />
                    <path d="M7.5 20.5 19 9l-4-4L3.5 16.5 2 22z" />
                  </svg>
                </button>
              )}
            </>
          )}
        </div>
      </td>
    </tr>
  );
}
